import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileBox, Plus, Trash2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/Dialog';
import { Input, Label } from '@/components/ui/Input';

interface PageTemplate {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  category: string;
  is_preset: boolean;
  preview_image: string | null;
}

export const Route = createFileRoute('/_app/page-templates')({
  component: TemplatesView,
});

const CATEGORY_LABELS: Record<string, string> = {
  landing: 'Landing',
  company: 'Empresa',
  utility: 'Utilidad',
  commerce: 'Comercio',
  service: 'Servicio',
  custom: 'Personalizadas',
};

function TemplatesView() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState<PageTemplate | null>(null);

  const { data: templates } = useQuery({
    queryKey: ['page-templates'],
    queryFn: () => unwrap<PageTemplate[]>(api.get('/page-templates')),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/page-templates/${id}`),
    onSuccess: () => {
      toast.success('Plantilla eliminada');
      void qc.invalidateQueries({ queryKey: ['page-templates'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'No se pudo eliminar'),
  });

  const grouped = (templates ?? []).reduce<Record<string, PageTemplate[]>>((acc, t) => {
    (acc[t.category] ??= []).push(t);
    return acc;
  }, {});

  return (
    <div className="mx-auto max-w-7xl p-6 md:p-8">
      <header className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Plantillas de página</h1>
          <p className="text-sm text-neutral-500">
            Empieza páginas nuevas en segundos con plantillas pre-diseñadas.
          </p>
        </div>
      </header>

      {Object.keys(grouped).length === 0 && (
        <Card>
          <CardContent className="py-16 text-center text-sm text-neutral-500">
            <FileBox className="mx-auto mb-2 size-8 text-neutral-300" />
            No hay plantillas todavía.
          </CardContent>
        </Card>
      )}

      {Object.entries(grouped).map(([cat, items]) => (
        <section key={cat} className="mb-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-neutral-500">
            {CATEGORY_LABELS[cat] ?? cat}
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {items.map((t) => (
              <Card key={t.id} className="group transition-all hover:shadow-md">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <FileBox className="size-4 text-neutral-400" />
                      <CardTitle className="text-base">{t.name}</CardTitle>
                    </div>
                    {t.is_preset && <Badge variant="outline" className="text-[10px]">Preset</Badge>}
                  </div>
                  <CardDescription>{t.description}</CardDescription>
                </CardHeader>
                <CardContent className="flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={() => setCreateOpen(t)}
                  >
                    <Sparkles className="size-3.5" /> Usar plantilla
                  </Button>
                  {!t.is_preset && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2 text-red-600 hover:bg-red-50"
                      onClick={() => {
                        if (confirm('¿Eliminar plantilla?')) remove.mutate(t.id);
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      ))}

      <CreateFromTemplate
        template={createOpen}
        onClose={() => setCreateOpen(null)}
        onCreated={(id) => navigate({ to: '/pages/$id', params: { id } })}
      />
    </div>
  );
}

function CreateFromTemplate({
  template, onClose, onCreated,
}: {
  template: PageTemplate | null;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [locale, setLocale] = useState('es');

  const create = useMutation({
    mutationFn: () => unwrap<{ id: string }>(api.post('/pages', {
      title, slug, locale, status: 'draft', from_template_id: template?.id,
    })),
    onSuccess: (p) => {
      toast.success('Página creada desde plantilla');
      onCreated(p.id);
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Error al crear página'),
  });

  if (!template) return null;

  return (
    <Dialog open={Boolean(template)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nueva página desde "{template.name}"</DialogTitle>
          <DialogDescription>{template.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Título</Label>
            <Input
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (!slug || slug === slugify(title)) setSlug(slugify(e.target.value));
              }}
              placeholder="Título de la página"
            />
          </div>
          <div>
            <Label>URL (slug)</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-400">/</span>
              <Input value={slug} onChange={(e) => setSlug(slugify(e.target.value))} placeholder="mi-pagina" />
            </div>
          </div>
          <div>
            <Label>Idioma</Label>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              className="w-full rounded border border-neutral-200 px-3 py-2 text-sm"
            >
              <option value="es">Español</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            disabled={!title || !slug || create.isPending}
            onClick={() => create.mutate()}
          >
            <Sparkles className="size-4" /> Crear página
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}
